// Measurement formatting — port of iOS `ScanFormat` (in ScanGeometry.swift).
// Values are always stored in meters; these render them metric or imperial.

import { useCallback, useEffect, useState } from "react";

export type MeasurementSystem = "metric" | "imperial";

const M_TO_IN = 39.3700787;
const M_TO_FT = 3.2808399;
const SQM_TO_SQFT = 10.7639104;

/** "12′ 4″" or "3.76 m" (metric: 2 dp under 10 m, else 1 dp). */
export function formatLength(meters: number, system: MeasurementSystem): string {
  if (system === "metric") {
    return `${meters.toFixed(meters < 10 ? 2 : 1)} m`;
  }
  const totalInches = Math.round(meters * M_TO_IN);
  let feet = Math.trunc(totalInches / 12);
  let inches = totalInches % 12;
  if (inches === 12) {
    feet += 1;
    inches = 0;
  }
  if (feet === 0) return `${inches}″`;
  return inches === 0 ? `${feet}′` : `${feet}′ ${inches}″`;
}

/** Short form for cramped floorplan labels: "12.3′" or "3.8 m". */
export function compactLength(meters: number, system: MeasurementSystem): string {
  if (system === "metric") return `${meters.toFixed(1)} m`;
  return `${(meters * M_TO_FT).toFixed(1)}′`;
}

function reduceFraction(n: number, d: number): [number, number] {
  let a = n;
  let b = d;
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  const g = Math.max(a, 1);
  return [n / g, d / g];
}

/**
 * Construction-rounded length: imperial snapped to the nearest 1/8″ as `F′-I n/d″`,
 * metric to the nearest 1 cm. Display only — callers keep full-precision meters.
 */
export function constructionLength(meters: number, system: MeasurementSystem): string {
  if (system === "metric") {
    const cm = Math.round(meters * 100);
    return `${(cm / 100).toFixed(2)} m`;
  }
  const totalEighths = Math.max(0, Math.round(meters * M_TO_IN * 8));
  const feet = Math.trunc(totalEighths / 96);
  const remEighths = totalEighths % 96;
  const inches = Math.trunc(remEighths / 8);
  const eighths = remEighths % 8;
  const parts: string[] = [];
  if (feet > 0) parts.push(`${feet}′`);
  let inchStr = "";
  if (eighths === 0) {
    if (inches > 0 || feet === 0) inchStr = `${inches}″`;
  } else {
    const [num, den] = reduceFraction(eighths, 8);
    inchStr = inches > 0 ? `${inches} ${num}/${den}″` : `${num}/${den}″`;
  }
  if (inchStr) parts.push(inchStr);
  return parts.length === 0 ? "0″" : parts.join("-");
}

/** "184 sq ft" or "17.1 m²". */
export function formatArea(squareMeters: number, system: MeasurementSystem): string {
  if (system === "metric") return `${squareMeters.toFixed(1)} m²`;
  return `${Math.round(squareMeters * SQM_TO_SQFT)} sq ft`;
}

/** "8′ 2″ × 3′ 1″ × 2′ 6″" (w × h × d) for furniture. */
export function formatDimensions(dims: number[], system: MeasurementSystem): string {
  return dims.map((d) => formatLength(d, system)).join(" × ");
}

// MARK: - React hook

const STORAGE_KEY = "scan.measurementSystem";

/** Locale default: US/Liberia/Myanmar → imperial, everyone else → metric. */
function localeDefault(): MeasurementSystem {
  if (typeof navigator === "undefined") return "imperial";
  const lang = (navigator.language || "").toUpperCase();
  // Region suffix after the dash (e.g. "en-US" → "US").
  const region = lang.includes("-") ? lang.split("-")[1] : "";
  return region === "US" || region === "LR" || region === "MM" ? "imperial" : "metric";
}

function readStored(): MeasurementSystem {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "metric" || stored === "imperial") return stored;
  }
  return localeDefault();
}

/** Measurement system with a toggle; persists the choice to localStorage. */
export function useMeasurementSystem(): {
  system: MeasurementSystem;
  toggle: () => void;
} {
  const [system, setSystem] = useState<MeasurementSystem>(() => readStored());

  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, system);
    }
  }, [system]);

  const toggle = useCallback(() => {
    setSystem((s) => (s === "metric" ? "imperial" : "metric"));
  }, []);

  return { system, toggle };
}
